/* eslint-disable indent */
import React from "react";
import Link from "next/link";
import { ImageJsonLd, NextSeo } from "next-seo";
import { ImagePage as ImagePageComponent } from "@/components/molecules/ImagePage";
import { getImage } from "lib/actions";
import { ImageDTO } from "types";
const getSeoData = (image: ImageDTO | null, imageId: string) => {
  if (!image) {
    return {
      title: "Fantasy Image Not Found | CharGen",
      description: "The requested fantasy character image could not be found. Explore our gallery for more D&D artwork.",
      canonical: `https://char-gen.com/images/${imageId}`,
      noindex: true
    };
  }
  const {
    imageMetadata,
    image: imageUrl,
    webpImage
  } = image;
  const {
    username,
    description,
    npcFieldsMap = {},
    requestType = ""
  } = imageMetadata;

  // Create a single mapping of all the possible entity fields
  const entityParts = [npcFieldsMap["Race"], npcFieldsMap["Class"], npcFieldsMap["Occupation"], npcFieldsMap["Monster"]].filter(Boolean);

  // Handle special cases with a single check
  let entityDescription = "";
  if (entityParts.length > 0) {
    entityDescription = entityParts.join(" ");
  } else if (npcFieldsMap["Item Name"]) {
    entityDescription = npcFieldsMap["Item Type"] ? `${npcFieldsMap["Item Type"]} - ${npcFieldsMap["Item Name"]}` : npcFieldsMap["Item Name"];
  } else {
    // Use a simplified lookup for other types
    entityDescription = npcFieldsMap["Landscape Name"] || npcFieldsMap["Battlemap Name"] || "";
  }

  // Create formatted type just once
  const formattedType = requestType ? `${requestType.charAt(0).toUpperCase()}${requestType.slice(1).toLowerCase()}` : "";

  // Create title with minimal operations
  const title = entityDescription ? `${entityDescription} | ${formattedType} Fantasy Art | CharGen` : `Fantasy ${formattedType} Art | Free AI Fantasy Generator | CharGen`;

  // Simplified description logic
  const detailedDescription = description || `AI-generated ${formattedType.toLowerCase()} fantasy ${entityDescription ? `art of ${entityDescription.toLowerCase()}` : "artwork created with CharGen's AI image generator"}${username ? ` by ${username}` : ""}`;
  const seoData = {
    title,
    description: detailedDescription,
    canonical: `https://char-gen.com/images/${imageId}`,
    imageUrl,
    webpImage,
    entityDescription: entityDescription || formattedType,
    keywords: `${formattedType.toLowerCase()}, fantasy art, D&D artwork${entityDescription ? `, ${entityDescription.toLowerCase()}` : ""}`,
    noindex: false
  };
  return seoData;
};
export async function getServerSideProps({
  params
}: {
  params: {
    imageId: string;
  };
}) {
  const imageId = params.imageId;
  try {
    const {
      image,
      error
    } = await getImage(imageId as string);
    const seoData = getSeoData(image ?? null, imageId);
    return {
      props: {
        image: image ?? null,
        error: error ?? null,
        imageId,
        seoData
      }
    };
  } catch (err) {
    console.error("Error in getServerSideProps:", err);
    return {
      props: {
        image: null,
        error: "Failed to load image",
        imageId
      }
    };
  }
}
export default function ImagePage({
  image,
  error,
  seoData
}: {
  image: ImageDTO | null;
  error: string | null | {
    error?: string;
    [key: string]: any;
  };
  seoData: {
    title: string;
    description: string;
    canonical: string;
    imageUrl: string;
    webpImage: string;
    entityDescription: string;
    keywords: string;
    noindex: boolean;
  };
}) {
  if (error) {
    const errorMessage = typeof error === "string" ? error : (error as any).error || JSON.stringify(error);
    return <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4">
                <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-xl p-8 shadow-xl max-w-lg w-full">
                    <h2 className="text-xl font-bold text-white mb-4">Error</h2>
                    <p className="text-slate-400">{errorMessage}</p>
                    <Link href="/gallery" className="mt-6 inline-flex items-center justify-center rounded-md bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white shadow hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900 transition-colors">
                        Return to Gallery
                    </Link>
                </div>
            </div>;
  }
  if (!image) {
    return <>
                <NextSeo title={seoData.title} description={seoData.description} canonical={seoData.canonical} noindex={true} openGraph={{
        url: seoData.canonical,
        title: seoData.title,
        description: seoData.description
      }} />
                <div>
                    <div className="min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center bg-gradient-to-b from-slate-950 via-slate-900 to-indigo-950/30 px-4 py-16">
                        <div className="max-w-3xl w-full relative">
                            {/* Decorative elements */}
                            <div className="absolute inset-0 -z-10 opacity-30 overflow-hidden">
                                <div className="absolute -top-20 -left-20 w-72 h-72 bg-indigo-500/20 rounded-full blur-3xl" />
                                <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl" />
                            </div>

                            <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-xl p-8 shadow-xl">
                                <div className="flex flex-col items-center text-center space-y-6">
                                    <div className="relative w-20 h-20 flex items-center justify-center rounded-full bg-slate-800/70 border border-slate-700">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                        </svg>
                                        <span className="absolute w-full h-full rounded-full border-t-2 border-indigo-500 animate-spin" />
                                    </div>

                                    <div className="space-y-3">
                                        <h1 className="text-3xl font-bold text-white">
                                            Image Not Found
                                        </h1>
                                        <p className="text-slate-400 max-w-lg">
                                            The fantasy artwork you&apos;re
                                            looking for seems to have vanished
                                            into the mists. It may have been
                                            deleted, moved to another realm, or
                                            never existed in the first place.
                                        </p>
                                    </div>

                                    <div className="grid gap-4 pt-4 w-full max-w-md sm:grid-cols-2">
                                        <Link href="/gallery" className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white shadow hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900 transition-colors">
                                            Browse Gallery
                                        </Link>
                                        <Link href="/generate" className="inline-flex items-center justify-center rounded-md border border-slate-700 bg-slate-800 px-5 py-2.5 text-sm font-medium text-white shadow hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-slate-900 transition-colors">
                                            Create New Art
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 text-center text-sm text-slate-500">
                                <p>
                                    Want to explore more fantasy artwork? Check
                                    out our{" "}
                                    <Link href="/generation/dnd-character" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                                        character generator
                                    </Link>
                                    ,{" "}
                                    <Link href="/generation/dnd-monster" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                                        monster art
                                    </Link>
                                    , or{" "}
                                    <Link href="/generation/dnd-battlemap" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                                        battlemap creator
                                    </Link>
                                    .
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </>;
  }

  // Direct image preloading in head

  return <>
            <NextSeo title={seoData.title} description={seoData.description} canonical={seoData.canonical} noindex={seoData.noindex} openGraph={{
      url: seoData.canonical,
      title: seoData.title,
      description: seoData.description,
      images: seoData.imageUrl ? [{
        url: seoData.imageUrl,
        width: 1024,
        height: 1024,
        alt: `${seoData.entityDescription} Fantasy Artwork`,
        type: "image/png"
      }] : [],
      type: "article"
    }} twitter={{
      handle: "@CharGenAI",
      site: "@CharGenAI",
      cardType: "summary_large_image"
    }} additionalMetaTags={[{
      name: "keywords",
      content: seoData.keywords
    }]} data-sentry-element="NextSeo" data-sentry-source-file="index.tsx" />

            {image && seoData.imageUrl && <ImageJsonLd images={[{
      contentUrl: seoData.imageUrl,
      creator: {
        "@type": "Person",
        name: image.imageMetadata.username || "CharGen User"
      },
      creditText: `Image created with CharGen AI by ${image.imageMetadata.username || "a user"}`,
      license: "https://char-gen.com/terms",
      acquireLicensePage: "https://char-gen.com/pricing"
    }]} />}

            {/* Critical Path: Render ImagePage Component directly */}
            <ImagePageComponent imageDto={image} metaTitle={seoData.title} metaDescription={seoData.description} data-sentry-element="ImagePageComponent" data-sentry-source-file="index.tsx" />
        </>;
}